import type { RangeSource } from "./rangeSource";

/**
 * Reads entries out of a ZIP archive using byte ranges, without downloading the archive.
 *
 * Photogrammetry mesh exports arrive as one archive holding dozens of OBJ/MTL/JPG tiles —
 * in the reference dataset 1.8 GB across 181 entries, all *stored* rather than deflated.
 * Pulling a single tile is then one ranged GET with no decompression, which is what makes
 * lazy per-tile loading of such an archive practical in a browser.
 */

const SIG_EOCD = 0x06054b50;
const SIG_EOCD64_LOCATOR = 0x07064b50;
const SIG_EOCD64 = 0x06064b50;
const SIG_CENTRAL = 0x02014b50;
const SIG_LOCAL = 0x04034b50;

const EOCD_MIN_SIZE = 22;
const MAX_COMMENT = 0xffff;
const ZIP64_SENTINEL_32 = 0xffffffff;
const ZIP64_SENTINEL_16 = 0xffff;

export interface ZipEntry {
  name: string;
  compressionMethod: number;
  compressedSize: number;
  uncompressedSize: number;
  localHeaderOffset: number;
  /** DOS date/time converted to a JS date, when present. */
  modified?: Date;
}

export class ZipReader {
  private entries: ZipEntry[] = [];
  private byName = new Map<string, ZipEntry>();
  /** Offset of the entry payload, resolved lazily from the local file header. */
  private dataOffsets = new Map<string, number>();

  private constructor(private readonly source: RangeSource) {}

  static async open(source: RangeSource): Promise<ZipReader> {
    const reader = new ZipReader(source);
    await reader.readCentralDirectory();
    return reader;
  }

  list(): readonly ZipEntry[] {
    return this.entries;
  }

  get(name: string): ZipEntry | undefined {
    return this.byName.get(name);
  }

  /** Entries whose file name (ignoring directories) matches a predicate. */
  find(predicate: (name: string, entry: ZipEntry) => boolean): ZipEntry[] {
    return this.entries.filter((e) => predicate(baseName(e.name), e));
  }

  async read(entry: ZipEntry | string, signal?: AbortSignal): Promise<Uint8Array> {
    const resolved = typeof entry === "string" ? this.byName.get(entry) : entry;
    if (!resolved) throw new Error(`Zip entry not found: ${String(entry)}`);

    const dataOffset = await this.resolveDataOffset(resolved, signal);
    const raw = await this.source.read(dataOffset, resolved.compressedSize, signal);

    if (resolved.compressionMethod === 0) return raw;
    if (resolved.compressionMethod === 8) return inflateRaw(raw);
    throw new Error(
      `Zip entry "${resolved.name}" uses unsupported compression method ${resolved.compressionMethod}`,
    );
  }

  async readText(entry: ZipEntry | string, signal?: AbortSignal): Promise<string> {
    return new TextDecoder().decode(await this.read(entry, signal));
  }

  /**
   * Reads a slice of an entry's *uncompressed* content. Only valid for stored entries;
   * used to sample vertex ranges out of very large OBJ tiles without reading them whole.
   */
  async readSlice(
    entry: ZipEntry | string,
    offset: number,
    length: number,
    signal?: AbortSignal,
  ): Promise<Uint8Array> {
    const resolved = typeof entry === "string" ? this.byName.get(entry) : entry;
    if (!resolved) throw new Error(`Zip entry not found: ${String(entry)}`);
    if (resolved.compressionMethod !== 0) {
      throw new Error(`Cannot slice deflated zip entry "${resolved.name}"`);
    }
    const start = Math.max(0, Math.min(offset, resolved.uncompressedSize));
    const end = Math.max(start, Math.min(offset + length, resolved.uncompressedSize));
    if (end === start) return new Uint8Array(0);
    const dataOffset = await this.resolveDataOffset(resolved, signal);
    return this.source.read(dataOffset + start, end - start, signal);
  }

  private async resolveDataOffset(entry: ZipEntry, signal?: AbortSignal): Promise<number> {
    const cached = this.dataOffsets.get(entry.name);
    if (cached !== undefined) return cached;

    // The local header repeats the name and may carry a differently sized extra field, so
    // the payload offset can only be derived by reading it.
    const header = await this.source.read(entry.localHeaderOffset, 30, signal);
    const view = new DataView(header.buffer, header.byteOffset, header.byteLength);
    if (view.getUint32(0, true) !== SIG_LOCAL) {
      throw new Error(`Corrupt zip: bad local header for "${entry.name}"`);
    }
    const nameLength = view.getUint16(26, true);
    const extraLength = view.getUint16(28, true);
    const offset = entry.localHeaderOffset + 30 + nameLength + extraLength;
    this.dataOffsets.set(entry.name, offset);
    return offset;
  }

  private async readCentralDirectory(): Promise<void> {
    const size = await this.source.size();
    const tailLength = Math.min(size, EOCD_MIN_SIZE + MAX_COMMENT);
    const tail = await this.source.read(size - tailLength, tailLength);
    const tailView = new DataView(tail.buffer, tail.byteOffset, tail.byteLength);

    const eocdPos = findSignatureBackwards(tailView, SIG_EOCD, tail.length - EOCD_MIN_SIZE);
    if (eocdPos < 0) throw new Error(`${this.source.label}: not a ZIP archive (no end-of-directory record)`);

    let entryCount = tailView.getUint16(eocdPos + 10, true);
    let directorySize = tailView.getUint32(eocdPos + 12, true);
    let directoryOffset = tailView.getUint32(eocdPos + 16, true);

    if (
      entryCount === ZIP64_SENTINEL_16 ||
      directorySize === ZIP64_SENTINEL_32 ||
      directoryOffset === ZIP64_SENTINEL_32
    ) {
      const zip64 = await this.readZip64Directory(tail, tailView, eocdPos, size);
      entryCount = zip64.entryCount;
      directorySize = zip64.directorySize;
      directoryOffset = zip64.directoryOffset;
    }

    const directory = await this.source.read(directoryOffset, directorySize);
    this.parseCentralDirectory(directory, entryCount);
  }

  private async readZip64Directory(
    tail: Uint8Array,
    tailView: DataView,
    eocdPos: number,
    size: number,
  ): Promise<{ entryCount: number; directorySize: number; directoryOffset: number }> {
    const locatorPos = findSignatureBackwards(tailView, SIG_EOCD64_LOCATOR, eocdPos);
    if (locatorPos < 0) throw new Error(`${this.source.label}: ZIP64 locator not found`);
    const eocd64Offset = Number(tailView.getBigUint64(locatorPos + 8, true));

    // The ZIP64 record is usually inside the tail we already have; fall back to a read.
    const tailStart = size - tail.length;
    let view: DataView;
    let base: number;
    if (eocd64Offset >= tailStart) {
      view = tailView;
      base = eocd64Offset - tailStart;
    } else {
      const block = await this.source.read(eocd64Offset, 56);
      view = new DataView(block.buffer, block.byteOffset, block.byteLength);
      base = 0;
    }
    if (view.getUint32(base, true) !== SIG_EOCD64) {
      throw new Error(`${this.source.label}: corrupt ZIP64 end-of-directory record`);
    }
    return {
      entryCount: Number(view.getBigUint64(base + 32, true)),
      directorySize: Number(view.getBigUint64(base + 40, true)),
      directoryOffset: Number(view.getBigUint64(base + 48, true)),
    };
  }

  private parseCentralDirectory(directory: Uint8Array, entryCount: number): void {
    const view = new DataView(directory.buffer, directory.byteOffset, directory.byteLength);
    const utf8 = new TextDecoder("utf-8");
    // Names in archives without the UTF-8 flag are officially CP437; latin1 keeps ASCII
    // exact, which is all these datasets use.
    const legacy = new TextDecoder("windows-1252");

    let cursor = 0;
    for (let i = 0; i < entryCount && cursor + 46 <= directory.length; i++) {
      if (view.getUint32(cursor, true) !== SIG_CENTRAL) {
        throw new Error(`Corrupt zip: bad central directory entry ${i}`);
      }
      const flags = view.getUint16(cursor + 8, true);
      const compressionMethod = view.getUint16(cursor + 10, true);
      const dosTime = view.getUint16(cursor + 12, true);
      const dosDate = view.getUint16(cursor + 14, true);
      let compressedSize = view.getUint32(cursor + 20, true);
      let uncompressedSize = view.getUint32(cursor + 24, true);
      const nameLength = view.getUint16(cursor + 28, true);
      const extraLength = view.getUint16(cursor + 30, true);
      const commentLength = view.getUint16(cursor + 32, true);
      let localHeaderOffset = view.getUint32(cursor + 42, true);

      const nameBytes = directory.subarray(cursor + 46, cursor + 46 + nameLength);
      const name = (flags & 0x800 ? utf8 : legacy).decode(nameBytes);

      const extraStart = cursor + 46 + nameLength;
      if (
        uncompressedSize === ZIP64_SENTINEL_32 ||
        compressedSize === ZIP64_SENTINEL_32 ||
        localHeaderOffset === ZIP64_SENTINEL_32
      ) {
        const zip64 = readZip64Extra(
          view,
          extraStart,
          extraLength,
          uncompressedSize === ZIP64_SENTINEL_32,
          compressedSize === ZIP64_SENTINEL_32,
          localHeaderOffset === ZIP64_SENTINEL_32,
        );
        uncompressedSize = zip64.uncompressedSize ?? uncompressedSize;
        compressedSize = zip64.compressedSize ?? compressedSize;
        localHeaderOffset = zip64.localHeaderOffset ?? localHeaderOffset;
      }

      if (!name.endsWith("/")) {
        const entry: ZipEntry = {
          name,
          compressionMethod,
          compressedSize,
          uncompressedSize,
          localHeaderOffset,
          modified: dosDateToDate(dosDate, dosTime),
        };
        this.entries.push(entry);
        this.byName.set(name, entry);
      }
      cursor = extraStart + extraLength + commentLength;
    }
  }
}

function readZip64Extra(
  view: DataView,
  start: number,
  length: number,
  needUncompressed: boolean,
  needCompressed: boolean,
  needOffset: boolean,
): { uncompressedSize?: number; compressedSize?: number; localHeaderOffset?: number } {
  let cursor = start;
  const end = start + length;
  while (cursor + 4 <= end) {
    const headerId = view.getUint16(cursor, true);
    const dataSize = view.getUint16(cursor + 2, true);
    if (headerId === 0x0001) {
      let field = cursor + 4;
      const out: { uncompressedSize?: number; compressedSize?: number; localHeaderOffset?: number } = {};
      if (needUncompressed && field + 8 <= end) {
        out.uncompressedSize = Number(view.getBigUint64(field, true));
        field += 8;
      }
      if (needCompressed && field + 8 <= end) {
        out.compressedSize = Number(view.getBigUint64(field, true));
        field += 8;
      }
      if (needOffset && field + 8 <= end) {
        out.localHeaderOffset = Number(view.getBigUint64(field, true));
      }
      return out;
    }
    cursor += 4 + dataSize;
  }
  return {};
}

function findSignatureBackwards(view: DataView, signature: number, from: number): number {
  for (let i = Math.min(from, view.byteLength - 4); i >= 0; i--) {
    if (view.getUint32(i, true) === signature) return i;
  }
  return -1;
}

function dosDateToDate(dosDate: number, dosTime: number): Date | undefined {
  if (!dosDate) return undefined;
  return new Date(
    1980 + ((dosDate >> 9) & 0x7f),
    ((dosDate >> 5) & 0x0f) - 1,
    dosDate & 0x1f,
    (dosTime >> 11) & 0x1f,
    (dosTime >> 5) & 0x3f,
    (dosTime & 0x1f) * 2,
  );
}

async function inflateRaw(data: Uint8Array): Promise<Uint8Array> {
  if (typeof DecompressionStream === "undefined") {
    throw new Error("This browser cannot inflate deflated zip entries (no DecompressionStream)");
  }
  const stream = new Blob([data as BlobPart]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
  const buffer = await new Response(stream).arrayBuffer();
  return new Uint8Array(buffer);
}

export function baseName(path: string): string {
  const slash = Math.max(path.lastIndexOf("/"), path.lastIndexOf("\\"));
  return slash >= 0 ? path.slice(slash + 1) : path;
}

export function dirName(path: string): string {
  const slash = Math.max(path.lastIndexOf("/"), path.lastIndexOf("\\"));
  return slash >= 0 ? path.slice(0, slash + 1) : "";
}

export function extensionOf(path: string): string {
  const name = baseName(path);
  const dot = name.lastIndexOf(".");
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : "";
}
